import ListingFilterDefinition, {
  ListingFilterDefinitionOption,
  ListingFilterDefinitionOptionBadgeData,
  ListingFilterDefinitionOptionSelectData,
  ListingFilterDefinitionOptionSelectDataType,
} from "../../../../../common/components/listing/filter/types/listing-filter-definition";
import ListingSortDefinition, {
  ListingSortDefinitionOption,
} from "../../../../../common/components/listing/filter/types/listing-sort-definition";
import UserStatus from "../../../../../common/types/user-status";
import userHelper from "../../../../../common/utils/user/user.helper";
import userTranslationsHelper from "../../../../../languages/user-translations.helper";
import CargoOfficerListingFilter, {
  CargoOfficerListingCargoCompanyFilter,
  CargoOfficerListingEmailFilter,
  CargoOfficerListingFirstNameFilter,
  CargoOfficerListingLastNameFilter,
  CargoOfficerListingActivityStatusFilter,
  CargoOfficerListingUsernameFilter,
} from "../common/types/cargo-officer-listing-filter";
import CargoOfficerListingFilterType from "../common/types/cargo-officer-listing-filter-type";
import CargoOfficerListingSortKey from "../common/types/cargo-officer-listing-sort-key";

const cargoOfficerListingFilterHelper = () => {
  const translations =
    userTranslationsHelper.getCargoOfficerListingTranslations();

  const createBadgeData = (
    value: string,
    textTemplate: string,
    titleTemplate: string
  ): ListingFilterDefinitionOptionBadgeData => {
    return {
      text: textTemplate.replace("#{query}", value),
      title: titleTemplate.replace("#{query}", value),
    };
  };

  const getSelectDataByType = (
    filterType: CargoOfficerListingFilterType,
    query: string
  ) => {
    const options: Record<
      CargoOfficerListingFilterType,
      (query: string) => ListingFilterDefinitionOptionSelectData
    > = {
      [CargoOfficerListingFilterType.FIRST_NAME]: getSelectDataForFirstName,
      [CargoOfficerListingFilterType.LAST_NAME]: getSelectDataForLastName,
      [CargoOfficerListingFilterType.ACTIVITY_STATUS]: getSelectDataForStatus,
      [CargoOfficerListingFilterType.USERNAME]: getSelectDataForUserName,
      [CargoOfficerListingFilterType.EMAIL]: getSelectDataForEmailAddress,
      [CargoOfficerListingFilterType.CARGO_COMPANY]:
        getSelectDataForCargoCompany,
    };

    const result = options[filterType](query);

    return result;
  };

  const getSelectDataForFirstName = (
    query: string
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.firstNameTemplateLabel.replace(
            `#{query}`,
            query
          ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForLastName = (
    query: string
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.lastNameTemplateLabel.replace(
            `#{query}`,
            query
          ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForStatus =
    (): ListingFilterDefinitionOptionSelectData => {
      return {
        type: ListingFilterDefinitionOptionSelectDataType.DEFAULT,
        options: [
          {
            label:
              translations.filters.search.activityStatusTemplateLabel.replace(
                "#{query}",
                userHelper.getActivityStatusText(UserStatus.ACTIVE)
              ),
            value: UserStatus.ACTIVE,
          },
          {
            label:
              translations.filters.search.activityStatusTemplateLabel.replace(
                "#{query}",
                userHelper.getActivityStatusText(UserStatus.DELETED)
              ),
            value: UserStatus.DELETED,
          },
        ],
      };
    };

  const getSelectDataForUserName = (
    query: string
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.usernameTemplateLabel.replace(
            `#{query}`,
            query
          ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForEmailAddress = (
    query: string
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.emailTemplateLabel.replace(
            `#{query}`,
            query
          ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForCargoCompany = (
    query: string
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.cargoCompanyTemplateLabel.replace(
            `#{query}`,
            query
          ),
          value: query,
        },
      ],
    };
  };

  const getBadgeDefinitionByType = (
    filterType: CargoOfficerListingFilterType,
    filterValue: CargoOfficerListingFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    const options: Record<
      CargoOfficerListingFilterType,
      () => ListingFilterDefinitionOptionBadgeData
    > = {
      [CargoOfficerListingFilterType.FIRST_NAME]: () =>
        getBadgeForFirstName(
          filterValue as CargoOfficerListingFirstNameFilter["value"]
        ),
      [CargoOfficerListingFilterType.LAST_NAME]: () =>
        getBadgeForLastName(
          filterValue as CargoOfficerListingLastNameFilter["value"]
        ),
      [CargoOfficerListingFilterType.USERNAME]: () =>
        getBadgeForUserName(
          filterValue as CargoOfficerListingUsernameFilter["value"]
        ),
      [CargoOfficerListingFilterType.EMAIL]: () =>
        getBadgeForEmail(
          filterValue as CargoOfficerListingEmailFilter["value"]
        ),
      [CargoOfficerListingFilterType.CARGO_COMPANY]: () =>
        getBadgeForCargoCompanyName(
          filterValue as CargoOfficerListingCargoCompanyFilter["value"]
        ),
      [CargoOfficerListingFilterType.ACTIVITY_STATUS]: () =>
        getBadgeForActivityStatus(
          filterValue as CargoOfficerListingActivityStatusFilter["value"]
        ),
    };

    return options[filterType]();
  };

  const getBadgeForUserName = (
    filterValue: CargoOfficerListingUsernameFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.usernameLabel,
      translations.filters.usernameTitle
    );
  };

  const getBadgeForActivityStatus = (
    filterValue: CargoOfficerListingActivityStatusFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    const filterValueText = userHelper.getActivityStatusText(filterValue);

    return createBadgeData(
      filterValueText,
      translations.filters.activityStatusLabel,
      translations.filters.activityStatusTitle
    );
  };

  const getBadgeForCargoCompanyName = (
    filterValue: CargoOfficerListingCargoCompanyFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.cargoCompanyLabel,
      translations.filters.cargoCompanyTitle
    );
  };

  const getBadgeForFirstName = (
    filterValue: CargoOfficerListingFirstNameFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.firstNameLabel,
      translations.filters.firstNameTitle
    );
  };

  const getBadgeForLastName = (
    filterValue: CargoOfficerListingLastNameFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.lastNameLabel,
      translations.filters.lastNameTitle
    );
  };

  const getBadgeForEmail = (
    filterValue: CargoOfficerListingEmailFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.emailLabel,
      translations.filters.emailTitle
    );
  };

  const getFilterDefinitionByType = (
    filterType: CargoOfficerListingFilterType
  ): ListingFilterDefinitionOption<CargoOfficerListingFilter> => {
    return {
      getSelectData: (query: string) => getSelectDataByType(filterType, query),
      getBadgeData: (value) => getBadgeDefinitionByType(filterType, value),
      filterType,
    };
  };

  const getFilterDefinitionOptions =
    (): ListingFilterDefinitionOption<CargoOfficerListingFilter>[] => {
      return Object.values(CargoOfficerListingFilterType).map(
        getFilterDefinitionByType
      );
    };

  const getFilterDefinition =
    (): ListingFilterDefinition<CargoOfficerListingFilter> => {
      return {
        options: getFilterDefinitionOptions(),
      };
    };

  const getSortDefinition = (): ListingSortDefinition => {
    return {
      options: getSortDefinitionOptions(),
    };
  };

  const getSortDefinitionOptions = (): ListingSortDefinitionOption[] => {
    return [
      {
        label: translations.sort.firstNameAscLabel,
        value: CargoOfficerListingSortKey.FIRST_NAME_ASC,
      },
      {
        label: translations.sort.firstNameDescLabel,
        value: CargoOfficerListingSortKey.FIRST_NAME_DESC,
      },
      {
        label: translations.sort.lastNameAscLabel,
        value: CargoOfficerListingSortKey.LAST_NAME_ASC,
      },
      {
        label: translations.sort.lastNameDescLabel,
        value: CargoOfficerListingSortKey.LAST_NAME_DESC,
      },
      {
        label: translations.sort.usernameAscLabel,
        value: CargoOfficerListingSortKey.USERNAME_ASC,
      },
      {
        label: translations.sort.usernameDescLabel,
        value: CargoOfficerListingSortKey.USERNAME_DESC,
      },
    ];
  };

  return { getFilterDefinition, getSortDefinition };
};

export default cargoOfficerListingFilterHelper;
